import {
  format,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  addDays,
  parse,
  // eslint-disable-next-line import/no-duplicates
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
// eslint-disable-next-line import/no-duplicates
import { ja } from "date-fns/locale";

// 0埋めした文字列を返す
// num=値 length=桁数
export const zeroPadding = (num: number, length: number): string => {
  return String(num).padStart(length, "0");
};

// 2023年12月31日(月)といった文字列を返す
export const GetDateText = (date: Date): string => {
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${
    ["日", "月", "火", "水", "木", "金", "土"][date.getDay()]
  })`;
};

// 何分前、何時間前、何日前、何年前といった文字列を返す
// dateTime : iso-8601に準ずる(2023-12-31T13:50:40+09:00)
export const GetElapsedText = (dateTime: string): string => {
  const startDate = new Date(dateTime);
  const currentDate = new Date();
  const year = differenceInYears(currentDate, startDate);
  if (year > 0) {
    return `${year}年前`;
  }
  const month = differenceInMonths(currentDate, startDate);
  if (month > 1) {
    return `${month}ヶ月前`;
  }
  const day = differenceInDays(currentDate, startDate);
  if (day > 1) {
    return `${day}日前`;
  }
  const hours = differenceInHours(currentDate, startDate);
  if (hours > 0) {
    return `${hours}時間前`;
  }
  const minutes = differenceInMinutes(currentDate, startDate);
  if (minutes > 0) {
    return `${minutes}分前`;
  }
  return "1分前";
};

// MM月dd日(E)HH:SS配信済みという文字列を返す
// dateTime : iso-8601に準ずる(2023-12-31T13:50:40+09:00)
export const GetPublishedText = (dateTime: string): string => {
  const startDateTime = new Date(dateTime);
  return format(startDateTime, "MM/dd(E)HH:mm配信済み", { locale: ja });
};

// MM月dd日(E)HH:SS配信開始予定という文字列を返す
// dateTime : iso-8601に準ずる(2023-12-31T13:50:40+09:00)
export const GetScheduleText = (dateTime: string, withYear = false, simple = false): string => {
  const startDateTime = new Date(dateTime);
  let formatStr = "";
  if (simple) {
    formatStr = withYear ? "yyyy/MM/dd(E)HH:mm予定" : "MM/dd(E)HH:mm予定";
  } else {
    formatStr = withYear ? "yyyy年MM月dd日(E)HH:mm配信開始予定" : "MM月dd日(E)HH:mm配信開始予定";
  }
  return format(startDateTime, formatStr, { locale: ja });
};

// 経過時間「00:00:00」という文字列を返す
// startDateTime : iso-8601に準ずる(2023-12-31T13:50:40+09:00)
// endDateTime : iso-8601に準ずる(2023-12-31T13:50:40+09:00)
export const GetElapsedTimeText = (startDateTime: string, endDateTime: string | undefined = undefined): string => {
  const startDate = new Date(startDateTime);
  const currentDate = endDateTime ? new Date(endDateTime) : new Date();
  const seconds = differenceInSeconds(currentDate, startDate) % 60;
  if (seconds < 0) {
    return "00:00:00";
  }
  const hours = differenceInHours(currentDate, startDate);
  if (hours > 99) {
    return "99:59:59";
  }
  const minutes = differenceInMinutes(currentDate, startDate) % 60;

  return `${zeroPadding(hours, 2)}:${zeroPadding(minutes, 2)}:${zeroPadding(seconds, 2)}`;
};

// 経過日数を返す
// dateTime : YYYY-MM-DD
export const GetElapsedDays = (dateTime: string): number => {
  const startDate = new Date(dateTime);
  const currentDate = new Date();
  const tzOffsetMinutes = currentDate.getTimezoneOffset();
  currentDate.setMinutes(currentDate.getMinutes() - tzOffsetMinutes);
  return differenceInDays(currentDate, startDate);
};

// 指定のフォーマットで日本時間として日付文字列をフォーマットする
export const FormatDateString = (dateStr: string | Date | null | undefined, formatStr: string): string => {
  if (!dateStr) {
    return "";
  }
  const date = new Date(dateStr);
  return format(date, formatStr, { locale: ja });
};

// 有効期限日から解約日を取得する
export const getCancellationDateFromExpirationDate = (expirationDate: string): string => {
  if (!expirationDate) {
    return "";
  }
  const date = new Date(expirationDate);
  date.setDate(date.getDate() + 1);
  return format(date, "yyyy-MM-dd");
};

// 日付として有効かどうかを判定する
// ref: https://chaika.hatenablog.com/entry/2021/12/12/121200
export const IsValidDate = (dateStr: string, formatStr = "yyyy-MM-dd"): boolean => {
  const d = new Date(dateStr);
  try {
    // UTC でフォーマットすることでタイムゾーン差異を吸収
    const formatDate = formatInTimeZone(d, "UTC", formatStr);
    return dateStr === formatDate;
  } catch {
    return false;
  }
};

// 経過した満年数を返す (タイムゾーンによる時差を調整)
export const GetElapsedFullYears = (dateTime: string): number => {
  // 現状、 yyyy-MM-dd のみサポート
  // (時間やタイムゾーンも含まれると計算や判定方法が変わり要調整となるため)
  if (!IsValidDate(dateTime, "yyyy-MM-dd")) {
    throw new Error("Invalid date format");
  }

  const startDate = new Date(dateTime);
  const currentDate = new Date();
  const tzOffsetMinutes = currentDate.getTimezoneOffset();
  currentDate.setMinutes(currentDate.getMinutes() - tzOffsetMinutes);
  return differenceInYears(currentDate, startDate);
};

// 動画の時間の秒数を変換する
export const formatMoviePlayTime = (second: number, withFormat: boolean = false) => {
  const formats = withFormat ? ["時間", "分", "秒"] : [":", ":", ""];
  const seconds = zeroPadding(second % 60, 2);
  const minutes = zeroPadding(Math.floor((second / 60) % 60), 2);
  const minutesSeconds = `${minutes}${formats[1]}${seconds}${formats[2]}`;
  return second < 3600 ? minutesSeconds : `${Math.floor(second / 3600)}${formats[0]}${minutesSeconds}`;
};

// 銀行振込の期限を返す
export const getTranferDeadline = (): string => {
  return format(addDays(new Date(), 14), "MM月dd日(E)15:00", { locale: ja });
};

export const compareWithCurrentDate = (value: string): any => {
  const currentDate = new Date();
  const date = parse(value, "yyyy-MM-dd", new Date(), { locale: ja });
  return date.getTime() - currentDate.getTime();
};

export const getDeliverySchedule = (dateTime: string): string => {
  const startDateTime = new Date(dateTime);
  return format(startDateTime, "yyyy年MM月dd日 (E) HH:mm", { locale: ja });
};

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    zeroPadding: (num: number, length: number) => string;
    GetDateText: (date: Date) => string;
    GetElapsedText: (dateTime: string) => string;
    GetPublishedText: (dateTime: string) => string;
    GetScheduleText: (dateTime: string, withYear?: boolean, simple?: boolean) => string;
    GetElapsedTimeText: (startDateTime: string, endDateTime?: string | undefined) => string;
    GetElapsedDays: (dateTime: string) => number;
    FormatDateString: (dateStr: string | Date | null | undefined, formatStr: string) => string;
    getCancellationDateFromExpirationDate: (expirationDate: string) => string;
    IsValidDate: (dateStr: string, formatStr?: string) => boolean;
    GetElapsedFullYears: (dateTime: string) => number;
    formatMoviePlayTime: (second: number, withFormat?: boolean) => string;
    getTranferDeadline: () => string;
    compareWithCurrentDate: (value: string) => any;
    getDeliverySchedule: (dateTime: string) => string;
  }
}
